import { BackendApiClient } from './backend-client';
import type { AccountResponse, Account } from '../model/types';
import { DebugLogger } from '@/shared/lib/debug';
import { BackendValidationError } from '@/shared/lib/backend-errors';
import { requireList, UNREADABLE_RESPONSE_CODE } from '@/shared/lib/response-shape';

const backendClient = new BackendApiClient();

export async function getAccounts(sessionId: string, holderId: string, status?: Account['status']): Promise<AccountResponse> {
  try {
    // Create scope context for the holder
    const scopeContext = {
      scopeId: holderId,
      scopeType: 'holder' as const
    };
    
    const endpoint = status ? `/account?status=${status}` : '/account';
    const response = await backendClient.get(endpoint, sessionId, scopeContext);
    if (response.success && response.data) {
      return response.data as AccountResponse;
    } else {
      const err = new Error(response.error || 'Failed to fetch accounts');
      if (response.errorCode) (err as any).code = response.errorCode;
      throw err;
    }
  } catch (error) {
    if ((error as any).code) throw error;
    throw new Error(`Failed to fetch accounts: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get all accounts the user has access to, without scope restrictions
 * This is used when a user doesn't have account holder permissions but has direct account access
 */
export async function getAllAccounts(sessionId: string, status?: Account['status']): Promise<AccountResponse> {
  try {
    // Call without scope context (no Scope-Id or Scope-Type headers)
    const endpoint = status ? `/account?status=${status}` : '/account';
    const response = await backendClient.get(endpoint, sessionId, undefined);
    if (response.success && response.data) {
      return response.data as AccountResponse;
    } else {
      throw new Error(response.error || 'Failed to fetch accounts');
    }
  } catch (error) {
    throw new Error(`Failed to fetch accounts: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get the active accounts under a holder, falling back to the unscoped listing when the
 * caller may not list accounts in the holder scope but does have direct account access.
 */
export async function getActiveAccountsForHolder(sessionId: string, holderId: string): Promise<Account[]> {
  try {
    const response = await getAccounts(sessionId, holderId, 'active');
    return response.accounts;
  } catch (error) {
    if ((error as { code?: string }).code !== 'invalid-permission') {
      throw error;
    }

    DebugLogger.scope('No account list permission under holder scope, falling back to unscoped', { holderId });
    const response = await getAllAccounts(sessionId, 'active');
    return response.accounts.filter(account => account.holder_id === holderId);
  }
}

export async function getAccountById(sessionId: string, accountId: string): Promise<Account> {
  try {
    // Create scope context for the account
    const scopeContext = {
      scopeId: accountId,
      scopeType: 'account' as const
    };
    
    const response = await backendClient.get('/account', sessionId, scopeContext);
    if (response.success && response.data) {
      return response.data as Account;
    } else {
      throw new Error(response.error || 'Failed to fetch account');
    }
  } catch (error) {
    throw new Error(`Failed to fetch account: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export interface CreateAccountParams {
  name: string;
  unique_id: string;
}

/**
 * Create an account under an AccountHolder.
 *
 * Takes a holderId rather than a ScopeContext because the route accepts holder scope only —
 * passing the account-scoped context the console keeps in localStorage would be rejected.
 *
 * Deliberately has no outer try/catch, unlike its neighbours in this file. Their
 * `catch { throw new Error(...) }` re-wrap destroys the BackendValidationError instance, and
 * parseBackendErrors returns no fieldErrors for anything that is not one — which would drop the
 * per-field unique_id and name errors this endpoint exists to report.
 */
export async function createAccount(
  sessionId: string,
  holderId: string,
  params: CreateAccountParams
): Promise<Account> {
  const response = await backendClient.post('/account', sessionId, { accounts: [params] }, {
    scopeId: holderId,
    scopeType: 'holder',
  });

  if (!response.success || !response.data) {
    throw response.validationError ?? new BackendValidationError(response.error || 'Failed to create account');
  }

  const accounts = requireList<Account>(response.data, 'accounts', 'Unreadable account creation response');
  if (accounts.length === 0) {
    throw new BackendValidationError('Account creation returned no account', UNREADABLE_RESPONSE_CODE);
  }
  return accounts[0];
}

export function getActiveAccounts(accounts: Account[]): Account[] {
  // This function is now mainly for backward compatibility
  // Since we filter at the API level, this should return the same array
  return accounts.filter(account => account.status === 'active');
}

export function selectDefaultAccount(accounts: Account[]): Account | null {
  // Since we now filter at the API level, all accounts should be active
  if (accounts.length === 1) {
    return accounts[0];
  }
  
  return null; // User needs to select manually
}
